/**
 * Elimina las propiedades con valor `undefined` de un objeto.
 * Útil para enviar datos a Firestore, que no acepta `undefined`.
 */
export function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) result[key] = value;
  }
  return result as Partial<T>;
}

/**
 * Elimina las propiedades con valor `undefined` de forma recursiva, recorriendo
 * objetos y arreglos anidados. Útil para persistir documentos profundamente
 * anidados (ej: el documento de torneo con categorías → jugadores → grupos)
 * en Firestore, que rechaza cualquier `undefined` en cualquier nivel.
 *
 * Preserva instancias especiales (Date, Firestore Timestamp, etc.) sin recorrerlas.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined)
      .map(item => stripUndefinedDeep(item)) as unknown as T;
  }

  // Solo recorrer objetos planos; preservar Date, Timestamp y otras clases.
  if (value !== null && typeof value === 'object' && (value as any).constructor === Object) {
    const result: Record<string, any> = {};
    for (const [key, val] of Object.entries(value as Record<string, any>)) {
      if (val !== undefined) result[key] = stripUndefinedDeep(val);
    }
    return result as T;
  }

  return value;
}
